import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SearchBar } from "@/components/SearchBar";
import { ApplianceCard, ApplianceCategory } from "@/components/ApplianceCard";
import { CameraScanner, OCRResult } from "@/components/CameraScanner";
import { useProductImageUpgrade } from "@/hooks/useProductImageUpgrade";
import { useEprelSearch, productGroupToCategory } from "@/hooks/useEprelSearch";
import { supabase } from "@/integrations/supabase/client";
import { resolveScanDestination } from "@/lib/scanNavigation";
import aegWashingMachine from "@/assets/aeg-l8fb86es-washing-machine.webp";
import boschWashingMachine from "@/assets/bosch-washing-machine.png";
import bekoDryer from "@/assets/beko-b5t-dryer.webp";
import { toast } from "sonner";
import { resolveScannedDryingTechnology, type DryingTechnology } from "@/lib/dryerTechnology";
interface Appliance {
  id: string;
  name: string;
  modelNumber: string;
  category: ApplianceCategory;
  imageUrl?: string;
  typeShieldUrl?: string;
  // OCR extracted fields
  serialNumber?: string | null;
  productNumber?: string | null;
  type?: string | null;
  plateType?: string | null;
  fdNumber?: string | null;
  zNr?: string | null;
  plateVersion?: string | null;
  productionNumber?: string | null;
  specifications?: Record<string, string> | null;
  dryerTechnology?: DryingTechnology | null;
  dryerTechnologySource?: OCRResult['dryerTechnologySource'];
  productType?: string | null;
  // EPREL data
  eprelReference?: string | null;
  eprelData?: OCRResult['eprelData'];
  eprelClassification?: import("@/lib/eprelClassification").ApplianceClassification;
}


const STORAGE_KEY = "recentlyAccessedAppliances";

const getCategoryFromProductType = (productType?: string | null, modelNumber?: string | null, brand?: string | null): ApplianceCategory => {
  const model = (modelNumber || '').toUpperCase();
  const brandLower = brand?.toLowerCase() || '';

  // FIX-289: explicit paired wash/dry capacities must survive model heuristics.
  if (/washer[\s_-]?dryer/i.test(productType || '')) return "other";

  // Strong model-number signals override OCR productType (which can be wrong).
  // Miele heat-pump tumble dryers: HT (e.g. HT30-W), TW series (TWC, TWD, TWF, TWI, TWJ…)
  if ((brandLower.includes("miele") || brandLower === '') && /^HT\d/.test(model)) return "dryer";
  if (/^TW[A-Z]\d/.test(model) && (brandLower.includes("miele") || brandLower === '')) return "dryer";
  // Bosch/Siemens dryers: WT = Wäschetrockner
  if (/^WT[EGHSV]\d/.test(model)) return "dryer";
  // AEG dryers: T7/T8/T9 series
  if (/^T[789]\d/.test(model)) return "dryer";
  // Beko dryers: B3T/B5T patterns
  if (/^B[35]T\d/.test(model) && (brandLower.includes("beko") || brandLower === '')) return "dryer";

  // Check product type next
  if (productType) {
    const type = productType.toLowerCase();
    if (type.includes("dryer") || type.includes("tumble") || type.includes("trockn")) return "dryer";
    if (type.includes("wash") && !type.includes("dish")) return "washing-machine";
    if (type.includes("vacuum")) return "vacuum-cleaner";
    if (type.includes("dishwash")) return "dishwasher";
    if (type.includes("fridge") || type.includes("refriger")) return "refrigerator";
    if (type.includes("oven") || type.includes("stove") || type.includes("range") || type.includes("cooker") || type.includes("hob")) return "oven";
  }

  // Fallback: check model number patterns for known appliances
  if (modelNumber) {
    // Bosch/Siemens ovens: H = Herd (oven), B = Backofen (built-in oven)
    // HSG, HBG, HBA, HNG, HRG = steam/pyrolytic/standard ovens
    if (/^H[SBNG][AGBR]\d/.test(model)) return "oven";
    // Bosch/Siemens cooktops: P = Platte (cooktop), N = Induktion
    if (/^P[XIUV][EVXY]\d/.test(model)) return "oven";

    // AEG/Electrolux ovens: B = Built-in, BPK, BPE, BSK, BSE series
    if (/^B[PS][KE]\d/.test(model)) return "oven";

    // Miele ovens: H = Herd series
    if (/^H\d{4}/.test(model) && brandLower.includes("miele")) return "oven";

    // AEG washing machines: L followed by digit (e.g., L8FB86ES, L7FE, L6FB)
    if (/^L\d/.test(model)) return "washing-machine";

    // Bosch washing machines: WAE, WAN, WAW, WAS, WGG, WUU, etc.
    if (/^W[AGUNX][AEGNSTUVWY]\d/.test(model)) return "washing-machine";

    // Miele washing machines: W followed by digit
    if (/^W\d/.test(model) && brandLower.includes("miele")) return "washing-machine";

    // Bosch/Siemens dishwashers: S = Spülmaschine
    if (/^S[MBPNXR][AISUV]\d/.test(model)) return "dishwasher";

    // Miele dishwashers: G followed by 4 digits (e.g., G 2180, G2180, G 1182)
    if (/^G ?\d{4}/.test(model) && (brandLower.includes("miele") || brandLower === '')) return "dishwasher";

    // Bosch/Siemens fridges: K = Kühlschrank
    if (/^K[GIASTUV][VNDEFIS]\d/.test(model)) return "refrigerator";
  }

  return "other";
};

// Known local product images keyed by normalized model number
const knownProductImages: Record<string, string> = {
  "l8fb86es": aegWashingMachine,
  "wae28466sn": boschWashingMachine,
};

// Resolve the best image for an appliance: known local asset > stored imageUrl > undefined
const resolveApplianceImage = (appliance: { modelNumber: string; imageUrl?: string; name?: string; category?: string }): string | undefined => {
  const normalizedModel = appliance.modelNumber.toLowerCase().replace(/[\s\-\/]/g, '');
  for (const [key, img] of Object.entries(knownProductImages)) {
    if (normalizedModel.includes(key)) return img;
  }
  // Brand+category fallback
  if (!appliance.imageUrl) {
    const brand = (appliance.name || '').split(' ')[0]?.toUpperCase();
    const cat = appliance.category || '';
    if (cat === 'dryer' && brand === 'BEKO') return bekoDryer;
  }
  return appliance.imageUrl;
};

// A company with no history starts with an empty recents list — no sample data.
const defaultAppliances: Appliance[] = [];

const loadRecentAppliances = (): Appliance[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.length > 0) {
        // Re-evaluate categories so model-number signals (e.g. Miele HT30-W is a dryer)
        // can override an earlier wrong classification.
        return parsed.map((appliance: Appliance) => {
          if (/washer[\s_-]?dryer/i.test(appliance.productType || '')) return appliance;
          const corrected = getCategoryFromProductType(null, appliance.modelNumber, appliance.name);
          if (corrected !== "other" && corrected !== appliance.category) {
            return { ...appliance, category: corrected };
          }
          if (appliance.category === "other" && corrected !== "other") {
            return { ...appliance, category: corrected };
          }
          return appliance;
        });
      }
      return defaultAppliances;
    }
    return defaultAppliances;
  } catch {
    return defaultAppliances;
  }
};

const saveRecentAppliances = (appliances: Appliance[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(appliances));
  } catch (e) {
    console.error("Failed to save recent appliances:", e);
  }
};

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("home");
  const [searchQuery, setSearchQuery] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [recentAppliances, setRecentAppliances] = useState<Appliance[]>(loadRecentAppliances);
  const [isSearching, setIsSearching] = useState(false);
  const [knownBrands, setKnownBrands] = useState<string[]>([]);
  const { searchByModelAndBrand, searchOnlineProductInfo } = useEprelSearch();

  // Load known brand names from DB for smart query parsing (e.g. "Bosch WAE28466SN")
  useEffect(() => {
    let active = true;
    supabase
      .from('brands')
      .select('name')
      .then(({ data }) => {
        if (active && data) setKnownBrands(data.map((b) => b.name as string));
      });
    return () => { active = false; };
  }, []);

  // Process scan result forwarded from sidebar (or anywhere else)
  useEffect(() => {
    const state = location.state as { scannedTypeShieldUrl?: string; scannedResult?: OCRResult } | null;
    if (state?.scannedResult) {
      const forwarded = state;
      // Clear the state first so a downstream navigate() inside handleCapture isn't overridden
      navigate(location.pathname, { replace: true, state: {} });
      handleCapture(forwarded.scannedTypeShieldUrl || "", forwarded.scannedResult);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);


  // Callback to update appliance image when a better product shot is found
  const handleImageUpgrade = useCallback((applianceId: string, imageUrl: string) => {
    setRecentAppliances((prev) => {
      const updated = prev.map((a) =>
        a.id === applianceId ? { ...a, imageUrl } : a
      );
      saveRecentAppliances(updated);
      return updated;
    });
    console.log(`Upgraded image for appliance ${applianceId}`);
  }, []);

  // Hook to check and upgrade images to proper product shots (>70% appliance coverage)
  useProductImageUpgrade(recentAppliances, handleImageUpgrade);

  // Parse "Brand Model" or just "Model" from search query
  const parseSearchQuery = (query: string): { brand: string | null; modelNumber: string } => {
    const trimmed = query.trim();
    if (!trimmed) return { brand: null, modelNumber: '' };

    const lower = trimmed.toLowerCase();
    for (const brand of knownBrands) {
      const brandLower = brand.toLowerCase();
      if (lower.startsWith(brandLower + ' ') || lower.startsWith(brandLower + '\t')) {
        const remainder = trimmed.substring(brand.length).trim();
        if (remainder) return { brand, modelNumber: remainder };
      }
    }
    return { brand: null, modelNumber: trimmed };
  };

  const handleSearch = async () => {
    const query = searchQuery.trim();
    if (!query) return;

    const { brand, modelNumber } = parseSearchQuery(query);
    setIsSearching(true);
    const searchToast = toast.loading(
      brand ? t("toasts.searchingBrand", { brand, model: modelNumber }) : t("toasts.searchingModel", { model: modelNumber })
    );

    try {
      const tentativeCategory = getCategoryFromProductType(null, modelNumber, brand);
      const categoryLabel = tentativeCategory === 'washing-machine' ? 'Washing Machine'
        : tentativeCategory === 'dishwasher' ? 'Dishwasher'
        : tentativeCategory === 'refrigerator' ? 'Refrigerator'
        : tentativeCategory === 'oven' ? 'Oven'
        : tentativeCategory === 'dryer' ? 'Tumble Dryer'
        : 'Washing Machine';

      // Step 1: Try EPREL (official EU appliance database)
      const eprelResult = await searchByModelAndBrand(modelNumber, brand || '', categoryLabel);

      // Validate the EPREL match — the endpoint sometimes returns unrelated products
      // with success:true. Only accept the result if the supplier or model identifier
      // actually relates to what the user searched for.
      const norm = (s?: string | null) => (s || '').toLowerCase().replace(/[\s\-\/]/g, '');
      const userBrand = norm(brand);
      const userModel = norm(modelNumber);
      const matchedSupplier = norm(eprelResult?.data?.supplierOrTrademark);
      const matchedModel = norm(eprelResult?.data?.modelIdentifier);
      const eprelMatchIsValid =
        eprelResult?.success && eprelResult.data && (
          // Brand matches (either direction, to handle "Aeg" vs "AEG-Electrolux" etc.)
          (userBrand && matchedSupplier && (matchedSupplier.includes(userBrand) || userBrand.includes(matchedSupplier))) ||
          // OR the returned model identifier contains the user's model number
          (userModel && matchedModel && matchedModel.includes(userModel))
        );

      if (eprelMatchIsValid) {
        const data = eprelResult.data;
        const detectedCategory = (eprelResult.productGroup
          ? productGroupToCategory(eprelResult.productGroup)
          : null) as ApplianceCategory | null;
        const finalCategory: ApplianceCategory = detectedCategory || tentativeCategory;
        const supplier = data.supplierOrTrademark || brand || '';
        const newAppliance: Appliance = {
          id: `searched-${Date.now()}`,
          name: `${supplier} ${data.modelIdentifier || modelNumber}`.trim(),
          modelNumber: data.modelIdentifier || modelNumber,
          category: finalCategory,
          eprelReference: data.eprelRegistrationNumber,
          eprelData: {
            energyClass: data.energyClass,
            ratedCapacity: data.ratedCapacity,
            waterCons: data.waterCons,
            energyConsPer100Cycle: data.energyConsPer100Cycle,
            noise: data.noise,
            noiseClass: data.noiseClass,
            supplierOrTrademark: data.supplierOrTrademark,
          },
          eprelClassification: eprelResult.classification,
        };

        setRecentAppliances((prev) => {
          const filtered = prev.filter((a) => a.modelNumber !== newAppliance.modelNumber);
          const updated = [newAppliance, ...filtered].slice(0, 10);
          saveRecentAppliances(updated);
          return updated;
        });
        toast.success(t("toasts.foundEprel", { label: `${supplier} ${data.modelIdentifier || modelNumber}` }), { id: searchToast });
        setSearchQuery("");
        return;
      }

      // Step 2: Fallback to online product info search (Firecrawl)
      if (brand) {
        toast.loading(t("toasts.fallbackOnline"), { id: searchToast });
        const online = await searchOnlineProductInfo(modelNumber, brand, categoryLabel);

        if (online?.success && online.data) {
          const newAppliance: Appliance = {
            id: `searched-${Date.now()}`,
            name: `${brand} ${modelNumber}`,
            modelNumber,
            category: tentativeCategory,
            specifications: online.data.specifications,
          };
          setRecentAppliances((prev) => {
            const filtered = prev.filter((a) => a.modelNumber !== newAppliance.modelNumber);
            const updated = [newAppliance, ...filtered].slice(0, 10);
            saveRecentAppliances(updated);
            return updated;
          });
          toast.success(t("toasts.foundOnline", { brand, model: modelNumber }), { id: searchToast });
          setSearchQuery("");
          return;
        }
      }

      // Step 3: Final fallback — add a basic entry so the user can still open it,
      // attach manuals, run workflows, etc. Better than failing silently.
      if (brand) {
        const fallbackAppliance: Appliance = {
          id: `searched-${Date.now()}`,
          name: `${brand} ${modelNumber}`,
          modelNumber,
          category: tentativeCategory,
        };
        setRecentAppliances((prev) => {
          const filtered = prev.filter((a) => a.modelNumber !== fallbackAppliance.modelNumber);
          const updated = [fallbackAppliance, ...filtered].slice(0, 10);
          saveRecentAppliances(updated);
          return updated;
        });
        toast.success(t("toasts.added", { brand, model: modelNumber }), {
          id: searchToast,
          description: t("toasts.addedDesc"),
        });
        setSearchQuery("");
        return;
      }

      toast.error(
        t("toasts.noData", { model: modelNumber }),
        { id: searchToast }
      );
    } catch (err) {
      console.error('Search error:', err);
      toast.error(t("toasts.searchFailed"), { id: searchToast });
    } finally {
      setIsSearching(false);
    }
  };

  const handleScan = () => {
    setScannerOpen(true);
  };

  const addScannedAppliance = (ocrResult: OCRResult, typeShieldUrl?: string): Appliance | null => {
    if (!ocrResult.modelNumber) return null;

    // Determine category from product type or model number
    let category = getCategoryFromProductType(ocrResult.productType, ocrResult.modelNumber, ocrResult.brand);
    
    // Use EPREL productGroup to correct category if we got "other"
    if (category === "other" && ocrResult.eprelData) {
      const eprelData = ocrResult.eprelData as Record<string, unknown>;
      const pg = ((eprelData.productGroup as string) || '').toLowerCase();
      if (pg.includes('tumbledryer') || pg.includes('dryer')) category = "dryer";
      else if (pg.includes('washingmachine')) category = "washing-machine";
      else if (pg.includes('dishwasher')) category = "dishwasher";
      else if (pg.includes('refriger')) category = "refrigerator";
      else if (pg.includes('oven')) category = "oven";
    }

    const newAppliance: Appliance = {
      id: `scanned-${Date.now()}`,
      name: ocrResult.brand 
        ? `${ocrResult.brand} ${ocrResult.productType || "Appliance"}`
        : ocrResult.productType || "Scanned Appliance",
      modelNumber: ocrResult.modelNumber,
      category: category,
      // Use product image URL if found, otherwise leave undefined
      imageUrl: ocrResult.productImageUrl || undefined,
      // Store the type shield URL from backend storage (not base64 anymore)
      typeShieldUrl: typeShieldUrl || undefined,
      // Store additional OCR fields
      serialNumber: ocrResult.serialNumber,
      productNumber: ocrResult.productNumber,
      type: ocrResult.type,
      plateType: ocrResult.plateType,
      fdNumber: ocrResult.fdCode,
      zNr: ocrResult.zNr,
      plateVersion: ocrResult.plateVersion,
      productionNumber: ocrResult.productionNumber,
      specifications: ocrResult.specifications,
      dryerTechnology: resolveScannedDryingTechnology(ocrResult.dryerTechnology, ocrResult.eprelClassification?.dryerTechnology),
      dryerTechnologySource: ocrResult.dryerTechnologySource,
      productType: ocrResult.productType,
      eprelClassification: ocrResult.eprelClassification,
      // Store EPREL data - only keep essential fields, not the full blob
      eprelReference: ocrResult.eprelReference,
      eprelData: ocrResult.eprelData ? {
        energyClass: ocrResult.eprelData.energyClass,
        ratedCapacity: ocrResult.eprelData.ratedCapacity,
        waterCons: ocrResult.eprelData.waterCons,
        energyConsPer100Cycle: ocrResult.eprelData.energyConsPer100Cycle,
        noise: ocrResult.eprelData.noise,
        noiseClass: ocrResult.eprelData.noiseClass,
        supplierOrTrademark: ocrResult.eprelData.supplierOrTrademark,
      } : undefined,
    };

    console.log('Adding scanned appliance:', newAppliance.name, 'Category:', category, 'Image:', newAppliance.imageUrl);

    setRecentAppliances((prev) => {
      // Remove duplicate if exists
      const filtered = prev.filter((a) => a.modelNumber !== newAppliance.modelNumber);
      // Add to front, limit to 10
      const updated = [newAppliance, ...filtered].slice(0, 10);
      saveRecentAppliances(updated);
      return updated;
    });

    return newAppliance;
  };




  const handleCapture = async (typeShieldUrl: string, ocrResult?: OCRResult) => {
    console.log("Captured type shield URL:", typeShieldUrl || "(none)");
    
    if (ocrResult) {
      console.log("OCR Result:", ocrResult);
      
      if (ocrResult.modelNumber) {
        setSearchQuery(ocrResult.modelNumber);
        toast.success(t("toasts.modelDetected", { model: ocrResult.modelNumber }));
        
        // Add to recently accessed with type shield URL
        const added = addScannedAppliance(ocrResult, typeShieldUrl || undefined);
        
        // Build info message with extracted details
        const details: string[] = [];
        if (ocrResult.brand) details.push(`Brand: ${ocrResult.brand}`);
        if (ocrResult.serialNumber) details.push(`S/N: ${ocrResult.serialNumber}`);
        if (ocrResult.productType) details.push(`Type: ${ocrResult.productType}`);
        
        if (details.length > 0) {
          toast.info(details.join(" | "));
        }
        
        // Show EPREL data toast if found
        if (ocrResult.eprelReference) {
          toast.success(t("toasts.eprelFound", { ref: ocrResult.eprelReference }));
        }

        const destination = await resolveScanDestination(ocrResult);

        if (destination && added?.dryerTechnology) {
          const { fetchCurrentCompanyId } = await import("@/hooks/useCurrentCompany");
          const companyId = await fetchCurrentCompanyId();
          if (companyId) {
            const { data: existing } = await supabase.from("appliances")
              .select("dryer_technology").eq("id", destination.applianceId).eq("company_id", companyId).maybeSingle();
            const technology = resolveScannedDryingTechnology(added.dryerTechnology, existing?.dryer_technology as DryingTechnology | null);
            const { error } = await supabase.from("appliances").update({ dryer_technology: technology })
              .eq("id", destination.applianceId).eq("company_id", companyId).select("id");
            if (error) console.error("Failed to save scanned drying technology:", error);
          }
        }

        // Navigate straight to the existing work order/appliance if found;
        // otherwise fall back to the newly scanned local appliance detail.
        if (added) {
          navigate(destination?.path || `/appliance/${added.id}`);
        } else if (destination) {
          navigate(destination.path);
        }
      } else if (ocrResult.rawText) {
        toast.info(t("toasts.textNoModel"));
      } else {
        toast.warning(t("toasts.noInfo"));
      }
    } else {
      toast.warning(t("toasts.noOcrResults"));
    }
  };

  const handleApplianceClick = (appliance: Appliance) => {
    navigate(`/appliance/${appliance.id}`);
  };

  const handleDeleteAppliance = (applianceId: string) => {
    setRecentAppliances((prev) => {
      const updated = prev.filter((a) => a.id !== applianceId);
      saveRecentAppliances(updated);
      return updated;
    });
    toast.success(t("toasts.applianceRemoved"));
  };

  const handleCategoryChange = (applianceId: string, newCategory: ApplianceCategory) => {
    setRecentAppliances((prev) => {
      const updated = prev.map((a) => 
        a.id === applianceId ? { ...a, category: newCategory } : a
      );
      saveRecentAppliances(updated);
      return updated;
    });
    toast.success(t("toasts.categoryUpdated"));
  };


  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="pt-12 pb-6 px-6 text-center">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          {t("header.title")}
        </h1>
        <p className="text-muted-foreground">
          {t("header.subtitle")}
        </p>
      </header>

      {/* Main Content */}
      <main className="px-6 max-w-4xl mx-auto space-y-8">
        {/* Search Section */}
        <section className="bg-card rounded-2xl p-6 shadow-sm border border-border">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={handleSearch}
            onScan={handleScan}
            isSearching={isSearching}
          />
        </section>

        {/* Recently Accessed */}
        <section className="bg-card rounded-2xl p-6 shadow-sm border border-border">
          <h2 className="text-lg font-semibold text-foreground mb-4">
            {t("recent.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recentAppliances.map((appliance) => (
              <ApplianceCard
                key={appliance.id}
                name={appliance.name}
                modelNumber={appliance.modelNumber}
                category={appliance.category}
                imageUrl={resolveApplianceImage(appliance)}
                onClick={() => handleApplianceClick(appliance)}
                onDelete={() => handleDeleteAppliance(appliance.id)}
                onCategoryChange={(cat) => handleCategoryChange(appliance.id, cat)}
              />
            ))}
          </div>
        </section>
      </main>

      {/* Camera Scanner */}
      <CameraScanner
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onCapture={handleCapture}
      />
    </div>
  );
};

export default Index;
